// Phase 7 D3 — Track C T-C5 + Track F T-F1. /settings/integrations page.
//
// Hosts the GmailConnectCard. Track F T-F1 lives here as the
// `?connect=success|denied|error` return-handler:
//
//   - useSearchParams reads the `connect` query param on mount + on
//     navigate-change.
//   - Each value emits a corresponding toast (Q-5 — toast-and-stay-on-page).
//   - After the toast fires once we strip the param via navigate(replace=true)
//     so subsequent re-renders or back/forward don't re-trigger it.
//
// Q-10: only destructive mutations (Disconnect) toast a confirmation. The
// success/denied/error toasts here are about an OAuth round-trip outcome,
// not a destructive action — but they exist because the SPA-unmount
// during OAuth means the user has no other feedback channel on return.

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { copy } from '@dailydeck/shared/copy';
import { endpoints } from '@dailydeck/shared/api';
import { useQueryClient } from '@tanstack/react-query';
import { GmailConnectCard } from './GmailConnectCard.js';
import { GMAIL_STATUS_QUERY_KEY } from '../../../hooks/useGmailStatus.js';
import {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
} from '../../../components/ui/Toast.js';
import { Button } from '../../../components/ui/Button.js';

type ConnectStatus = 'success' | 'denied' | 'error';

interface ConnectToastState {
  status: ConnectStatus;
}

function isConnectStatus(value: string | null): value is ConnectStatus {
  return value === 'success' || value === 'denied' || value === 'error';
}

export function Integrations(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.settings.shell.nav.integrations}`;
  }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [toastState, setToastState] = React.useState<ConnectToastState | null>(null);
  const lastHandledRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const param = searchParams.get('connect');
    if (param === null) return;
    // Guard against duplicate fires when the same query string is observed twice
    // in StrictMode dev double-mount (and on cache-restore returns).
    if (lastHandledRef.current === param) return;
    lastHandledRef.current = param;

    if (!isConnectStatus(param)) {
      // Unknown values are dropped silently; clear the param so subsequent
      // navigations are clean.
      navigate('/settings/integrations', { replace: true });
      return;
    }

    setToastState({ status: param });

    if (param === 'success') {
      // Refetch immediately so the card flips to connected.
      void queryClient.invalidateQueries({ queryKey: GMAIL_STATUS_QUERY_KEY });
    }

    // Strip the query string so the next render is clean.
    navigate('/settings/integrations', { replace: true });
  }, [searchParams, navigate, queryClient]);

  const toast = toastState;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h2
          id="settings-section-heading"
          className="text-title-2 font-semibold text-text-primary"
        >
          {copy.settings.integrations.title}
        </h2>
        <p className="text-body text-text-secondary mt-1">
          {copy.settings.integrations.description}
        </p>
      </header>

      <GmailConnectCard />

      {toast !== null && (
        <ToastProvider duration={5000}>
          <Toast
            open
            variant={toast.status === 'error' ? 'danger' : 'default'}
            onOpenChange={(open) => !open && setToastState(null)}
            data-testid={`gmail-toast-${toast.status}`}
          >
            <div className="flex flex-col gap-1">
              <ToastTitle>
                {toast.status === 'success' &&
                  copy.settings.integrations.connectSuccessToastTitle}
                {toast.status === 'denied' &&
                  copy.settings.integrations.connectDeniedToastTitle}
                {toast.status === 'error' &&
                  copy.settings.integrations.connectErrorToastTitle}
              </ToastTitle>
              <ToastDescription>
                {toast.status === 'success' &&
                  copy.settings.integrations.connectSuccessToastBody}
                {toast.status === 'denied' &&
                  copy.settings.integrations.connectDeniedToastBody}
                {toast.status === 'error' &&
                  copy.settings.integrations.connectErrorToastBody}
              </ToastDescription>
              {toast.status === 'error' && (
                <div className="pt-1">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      window.location.href = endpoints.paymentsEmailConnect;
                    }}
                  >
                    {copy.settings.integrations.connectRetryButton}
                  </Button>
                </div>
              )}
            </div>
            <ToastClose />
          </Toast>
          <ToastViewport />
        </ToastProvider>
      )}
    </div>
  );
}
